import { useEffect, useRef, useState } from "react";

/**
 * useLoadingIndicator — the one loading-indicator policy (ENG-APP-UX-TUNEUP
 * Track D, floor row 14).
 *
 * Source: docs/product/experience-and-ui/48-in-app-design-spec.md § 2
 * (loading) and the NN/g progress-indicator rule the ticket's floor table
 * cites: nothing is shown for a wait under the delay (a flash of skeleton on
 * a fast response is worse than none), a skeleton or spinner appears after
 * `delayMs` and then stays at least `minVisibleMs` so it never flickers, and
 * a wait past `determinateAfterMs` should switch to a determinate progress
 * surface — `longWait` tells the caller when.
 *
 * Pure timing; the caller renders whatever the surface needs (`Skeleton`,
 * a spinner, a progress bar). Consumers migrate by wave.
 */
export interface LoadingIndicatorOptions {
  /** Show nothing until the wait exceeds this (spec: 150–300 ms). */
  delayMs?: number;
  /** Once shown, keep the indicator at least this long (spec: ≥ 300 ms). */
  minVisibleMs?: number;
  /** Past this, a determinate indicator is due (spec: 10 s). */
  determinateAfterMs?: number;
}

export interface LoadingIndicatorState {
  /** Render the skeleton / spinner now. */
  show: boolean;
  /** The wait has passed the determinate threshold. */
  longWait: boolean;
}

export const LOADING_INDICATOR_DEFAULTS = {
  delayMs: 200,
  minVisibleMs: 300,
  determinateAfterMs: 10_000,
} as const;

export function useLoadingIndicator(
  isLoading: boolean,
  {
    delayMs = LOADING_INDICATOR_DEFAULTS.delayMs,
    minVisibleMs = LOADING_INDICATOR_DEFAULTS.minVisibleMs,
    determinateAfterMs = LOADING_INDICATOR_DEFAULTS.determinateAfterMs,
  }: LoadingIndicatorOptions = {},
): LoadingIndicatorState {
  const [show, setShow] = useState(false);
  const [longWait, setLongWait] = useState(false);
  const shownAtRef = useRef<number | null>(null);

  // Every state change happens inside a timer callback (never synchronously in
  // the effect body): the React Compiler lint forbids the cascade, and the
  // policy's transitions are all time-based anyway.
  useEffect(() => {
    if (isLoading) {
      const delay = window.setTimeout(() => {
        shownAtRef.current = Date.now();
        setShow(true);
      }, delayMs);
      const long = window.setTimeout(
        () => setLongWait(true),
        determinateAfterMs,
      );
      return () => {
        window.clearTimeout(delay);
        window.clearTimeout(long);
      };
    }
    const shownAt = shownAtRef.current;
    const remaining =
      shownAt === null ? 0 : Math.max(0, minVisibleMs - (Date.now() - shownAt));
    const hide = window.setTimeout(() => {
      shownAtRef.current = null;
      setShow(false);
      setLongWait(false);
    }, remaining);
    return () => window.clearTimeout(hide);
  }, [isLoading, delayMs, minVisibleMs, determinateAfterMs]);

  return { show, longWait };
}

export default useLoadingIndicator;
